import { Band, Eyebrow, Lead, Panel, Pill, QUIET_LINK } from '@/components/bedrock';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useT } from '@/core/I18nProvider';

import type { ReactElement, ReactNode } from 'react';

/**
 * "Where it works" — three light cards on the page ground.
 *
 * Deliberately the plainest band on the page, and placed directly after the
 * two demo bands for that reason: it is the visitor's first rest after ~40
 * seconds of motion. Three claims, no animation beyond the shared reveal.
 */
export function Reach(): ReactElement {
  const { t } = useT();

  return (
    <Band id="reach" space="lg" aria-labelledby="reach-title">
      {/* The heading is visually carried by the three card titles; a shared
          one would be a fourth competing title. It is present for the
          accessibility tree only, which is what `sr-only` is for. */}
      <h2 id="reach-title" className="sr-only">
        {t('home.reach.title')}
      </h2>

      <div data-bd-rise className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <ReachCard
          eyebrow={t('home.reach.inside.eyebrow')}
          title={t('home.reach.inside.title')}
          body={t('home.reach.inside.body')}
        >
          <Pill>{t('home.reach.inside.tag1')}</Pill>
          <Pill>{t('home.reach.inside.tag2')}</Pill>
          <Pill>{t('home.reach.inside.tag3')}</Pill>
        </ReachCard>

        <ReachCard
          eyebrow={t('home.reach.outside.eyebrow')}
          title={t('home.reach.outside.title')}
          body={t('home.reach.outside.body')}
        >
          {/* Assistant names are proper nouns — never translated, and never
              reordered by locale. */}
          <Pill dotColor="var(--bd-violet)">Claude</Pill>
          <Pill dotColor="var(--bd-ok)">ChatGPT</Pill>
          <Pill dotColor="var(--bd-cyan)">Gemini</Pill>
        </ReachCard>

        <ReachCard
          eyebrow={t('home.reach.back.eyebrow')}
          title={t('home.reach.back.title')}
          body={t('home.reach.back.body')}
        >
          <LocalizedLink to="/docs" className={`text-sm ${QUIET_LINK}`}>
            {t('home.reach.back.link')}{' '}
            <span aria-hidden="true" className="inline-block rtl:-scale-x-100">
              →
            </span>
          </LocalizedLink>
        </ReachCard>
      </div>
    </Band>
  );
}

interface ReachCardProps {
  readonly eyebrow: string;
  readonly title: string;
  readonly body: string;
  readonly children: ReactNode;
}

function ReachCard({ eyebrow, title, body, children }: ReachCardProps): ReactElement {
  return (
    <Panel className="flex flex-col p-7 sm:p-[30px]">
      <Eyebrow className="!tracking-[0.14em] rtl:!tracking-normal">{eyebrow}</Eyebrow>
      <h3 className="mt-3 text-balance text-[1.32rem] font-semibold tracking-[-0.03em] text-[color:var(--bd-ink)]">
        {title}
      </h3>
      <Lead className="mt-2.5 !text-[0.9rem]">{body}</Lead>
      {/* `mt-auto` pins the tag row to the card's foot so three cards with
          different body lengths still align along their bottom edge. */}
      <div className="mt-auto flex flex-wrap gap-2 pt-5">{children}</div>
    </Panel>
  );
}
